import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function UpdateInsurancePlan() {
  const navigate = useNavigate();
  const { state } = useLocation();


  // Form state
  const [formData, setFormData] = useState({
    name: "",
    insurerLogo: null,
    category_id: "",
    company_id: "",
    status: true,
    stats: [],
    minInvestYears: "",
    idealWithdrawYears: "",
    specialOffer: {
      text: "",
      bgGradient: "",
      textColor: "",
      bgColor: ""
    },
    features: [],
    existingImageUrl: "",
  });

  // State for categories from API
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await postData("api/categories/list", {
        page: 1,
        limit: 10000,
      });
      if (response?.status) {
        const categoriesList = response?.data || [];

        setCategories(categoriesList);
      } else {
        throw new Error(response?.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(error.message || "Error loading categories");
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await postData('api/company/list', {
        page: 1,
        limit: 10
      });
      if (response?.success) {
        setCompanies(response.data?.companies || []);
      } else {
        throw new Error(response?.message || "Failed to fetch companies");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Error loading companies"
      );
    } finally {
      setLoading(false);
    }
  };

  // Add a new stat
  const addStat = () => {
    setFormData((prev) => ({
      ...prev,
      stats: [...prev.stats, { label: "", value: "" }],
    }));
  };

  // Remove a stat
  const removeStat = (index) => {
    setFormData((prev) => {
      const newStats = [...prev.stats];
      newStats.splice(index, 1);
      return { ...prev, stats: newStats };
    });
  };

  // Add a new feature
  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, { text: "", color: "#008000" }],
    }));
  };

  // Remove a feature
  const removeFeature = (index) => {
    setFormData((prev) => {
      const newFeatures = [...prev.features];
      newFeatures.splice(index, 1);
      return { ...prev, features: newFeatures };
    });
  };

  useEffect(() => {
    fetchCategories();
    fetchCompanies();

    // Populate form data if editing existing insurance plan
    if (state?.subCategoryData) {
      const data = state.subCategoryData;
      setFormData({
        name: data.name || "",
        insurerLogo: null,
        category_id: data.category?._id || "",
        company_id: data.company?._id || "",
        status: data.status === "active",
        stats: data.stats || [],
        minInvestYears: data.minInvestYears || "",
        idealWithdrawYears: data.idealWithdrawYears || "",
        specialOffer: data.specialOffer || {
          text: "",
          bgGradient: "",
          textColor: "",
          bgColor: ""
        },
        features: data.features || [],
        existingImageUrl: data.insurerLogo || ""
      });
      setImagePreview(data.insurerLogo || "");
    }
  }, [state]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, image: file }));

      // Image preview for new upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (name === "name") {
      setFormData((prev) => ({ ...prev, name: value }));
    } else if (name === "category_id") {
      setFormData((prev) => ({ ...prev, category_id: value }));
    } else if (name === "company_id") {
      setFormData((prev) => ({ ...prev, company_id: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Upload image to API
  const uploadImage = async () => {
    if (!formData.image) return formData.existingImageUrl; // Return existing image if no new image
    const formDataObj = new FormData();
    formDataObj.append("file", formData.image);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", formDataObj);
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath;
      } else {
        toast.error(data.message || "Image upload failed");
        return null;
      }
    } catch (err) {
      toast.error("Network error during image upload");
      return null;
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Check category selected
      if (!formData.category_id) {
        throw new Error("Please select a category");
      }

      // Check company selected
      if (!formData.company_id) {
        throw new Error("Please select a company");
      }

      // Upload image if new one was selected
      const uploadedImagePath = await uploadImage();
      if (!uploadedImagePath) {
        throw new Error("Image upload failed");
      }

      // Prepare payload
      const payload = {
        id: state?.subCategoryData?._id,
        data: {
          name: formData.name,
          insurerLogo: uploadedImagePath,
          category: formData.category_id,
          company: formData.company_id,
          status: formData.status ? "active" : "inactive",
          stats: formData.stats || [],
          minInvestYears: formData.minInvestYears || "",
          idealWithdrawYears: formData.idealWithdrawYears || "",
          specialOffer: formData.specialOffer || {
            text: "",
            bgGradient: "",
            textColor: ""
          },
          features: formData.features || []
        }
      };

      // Call API to update insurance plan
      const response = await postData("api/insurance-plans/update", payload);
      if (response?.status) {
        toast.success("Insurance plan updated successfully!");
        navigate(-1);
      } else {
        throw new Error(response?.message || "Error updating insurance plan");
      }
    } catch (error) {
      console.error("Update Insurance Plan error:", error);
      toast.error(error.message || "Error updating insurance plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <h4 className="fs-18 fw-semibold m-0">Update Insurance Plan</h4>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Back
            </button>
          </div>

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Insurance Plan Details</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Category Dropdown */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="category_id" className="form-label">
                          Parent Category*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="category_id"
                          name="category_id"
                          className="form-control"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          required
                          disabled={loading || categories.length === 0}
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.title}
                            </option>
                          ))}
                        </select>
                        {loading && categories.length === 0 && (
                          <small className="text-muted">Loading categories...</small>
                        )}
                        {!loading && categories.length === 0 && (
                          <small className="text-danger">
                            No categories available. Please add categories first.
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Insurance Plan Name */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="name" className="form-label">
                          Plan Name*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="name"
                          name="name"
                          className="form-control"
                          placeholder="Enter insurance plan name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Company Dropdown */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="company_id" className="form-label">
                          Company*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="company_id"
                          name="company_id"
                          className="form-control"
                          value={formData.company_id}
                          onChange={handleInputChange}
                          required
                          disabled={loading || companies.length === 0}
                        >
                          <option value="">Select a company</option>
                          {companies.map((company) => (
                            <option key={company._id} value={company._id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                        {loading && companies.length === 0 && (
                          <small className="text-muted">Loading companies...</small>
                        )}
                        {!loading && companies.length === 0 && (
                          <small className="text-danger">
                            No companies available. Please add companies first.
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label">
                          Insurer Logo
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        <small className="text-muted">
                          Recommended size: 800x600px, Max size: 2MB
                        </small>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Insurer Logo Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label className="form-label">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="status"
                            checked={formData.status}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                status: !prev.status,
                              }))
                            }
                            role="switch"
                          />
                          <label className="form-check-label" htmlFor="status">
                            {formData.status ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-secondary">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Min Invest Years */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="minInvestYears" className="form-label">
                          Min Invest Years
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="minInvestYears"
                          name="minInvestYears"
                          className="form-control"
                          placeholder="e.g., 5"
                          value={formData.minInvestYears}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Ideal Withdraw Years */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="idealWithdrawYears" className="form-label">
                          Ideal Withdraw Years
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="idealWithdrawYears"
                          name="idealWithdrawYears"
                          className="form-control"
                          placeholder="e.g., 10"
                          value={formData.idealWithdrawYears}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label className="form-label">
                          Stats
                        </label>
                      </div>
                      <div className="col-md-9">
                        {formData.stats.map((stat, index) => (
                          <div key={index} className="row g-2 mb-2">
                            <div className="col">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Label (e.g., Life Cover)"
                                value={stat.label}
                                onChange={(e) => {
                                  const newStats = [...formData.stats];
                                  newStats[index].label = e.target.value;
                                  setFormData((prev) => ({ ...prev, stats: newStats }));
                                }}
                              />
                            </div>
                            <div className="col">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Value (e.g., ₹1 Cr)"
                                value={stat.value}
                                onChange={(e) => {
                                  const newStats = [...formData.stats];
                                  newStats[index].value = e.target.value;
                                  setFormData((prev) => ({ ...prev, stats: newStats }));
                                }}
                              />
                            </div>
                            <div className="col-auto">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => removeStat(index)}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={addStat}
                        >
                          Add Stat
                        </button>
                      </div>
                    </div>

                    {/* Special Offer */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label className="form-label">
                          Special Offer
                        </label>
                      </div>
                      <div className="col-md-9">
                        <div className="row g-2 mb-2">
                          <div className="col">
                            <input
                              type="text"
                              id="specialOffer.text"
                              name="specialOffer.text"
                              className="form-control"
                              placeholder="Text (e.g., Limited Period Offer)"
                              value={formData.specialOffer.text}
                              onChange={(e) => setFormData((prev) => ({
                                ...prev,
                                specialOffer: {
                                  ...prev.specialOffer,
                                  text: e.target.value
                                }
                              }))}
                            />
                          </div>
                          <div className="col">
                            <div className="d-flex align-items-center">
                              <input
                                type="color"
                                className="form-control form-control-color me-1"
                                title="Background color"
                                value={formData.specialOffer.bgGradient || "#fff5e6"}
                                onChange={(e) => {
                                  const bgColor = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    specialOffer: {
                                      ...prev.specialOffer,
                                      bgColor: bgColor,
                                      bgGradient: bgColor
                                    }
                                  }));
                                }}
                              />
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Background color code"
                                value={formData.specialOffer.bgGradient || ""}
                                onChange={(e) => {
                                  const bgColor = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    specialOffer: {
                                      ...prev.specialOffer,
                                      bgColor: bgColor,
                                      bgGradient: bgColor
                                    }
                                  }));
                                }}
                              />
                            </div>
                          </div>
                          <div className="col">
                            <div className="d-flex">
                              <input
                                type="color"
                                className="form-control form-control-color ms-2"
                                value={formData.specialOffer.textColor || "#000000"}
                                onChange={(e) => setFormData((prev) => ({
                                  ...prev,
                                  specialOffer: {
                                    ...prev.specialOffer,
                                    textColor: e.target.value
                                  }
                                }))}
                                title="Choose text color"
                              />
                              <input
                                type="text"
                                id="specialOffer.textColor"
                                name="specialOffer.textColor"
                                className="form-control"
                                placeholder="Text Color (e.g., #e67900)"
                                value={formData.specialOffer.textColor}
                                onChange={(e) => setFormData((prev) => ({
                                  ...prev,
                                  specialOffer: {
                                    ...prev.specialOffer,
                                    textColor: e.target.value
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label className="form-label">
                          Features
                        </label>
                      </div>
                      <div className="col-md-9">
                        {formData.features.map((feature, index) => (
                          <div key={index} className="row g-2 mb-2">
                            <div className="col">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Feature text"
                                value={feature.text}
                                onChange={(e) => {
                                  const newFeatures = [...formData.features];
                                  newFeatures[index].text = e.target.value;
                                  setFormData((prev) => ({ ...prev, features: newFeatures }));
                                }}
                              />
                            </div>
                            <div className="col">
                              <div className="d-flex">
                                <input
                                  type="color"
                                  className="form-control form-control-color me-1"
                                  title="Feature color"
                                  value={feature.color || "#008000"}
                                  onChange={(e) => {
                                    const newFeatures = [...formData.features];
                                    newFeatures[index].color = e.target.value;
                                    setFormData((prev) => ({ ...prev, features: newFeatures }));
                                  }}
                                />
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Color code"
                                  value={feature.color || ""}
                                  onChange={(e) => {
                                    const newFeatures = [...formData.features];
                                    newFeatures[index].color = e.target.value;
                                    setFormData((prev) => ({ ...prev, features: newFeatures }));
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-auto">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => removeFeature(index)}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={addFeature}
                        >
                          Add Feature
                        </button>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading || categories.length === 0 || companies.length === 0}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            "Update Insurance Plan"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}